import { createReducer, on } from '@ngrx/store';
import {
    createDefaultLoadable,
    createDefaultILoadableArray,
    onLoadableLoad,
    onLoadableSuccess,
    onLoadableError,
} from '../../models/loadable';
import { initialRegistryState } from './registry.state';
import { RegistryActions } from './registry.actions';
import { IBulkCreateEntryJobResult, IEntryJob } from '../../models/entry.interface';

const createdEntryJobs = (result: IBulkCreateEntryJobResult): IEntryJob[] => result.items
    .map(item => item.entry_job)
    .filter((entryJob): entryJob is IEntryJob => entryJob !== null);

export const registryReducer = createReducer(
    initialRegistryState,

    on(RegistryActions.loadRegistries, state => ({
        ...onLoadableLoad(state),
    })),
    on(RegistryActions.loadRegistriesSuccess, (state, { registries }) => ({
        ...onLoadableSuccess(state),
        registries,
    })),
    on(RegistryActions.loadRegistriesFailure, (state, { error }) => ({
        ...onLoadableError(state, error),
    })),

    on(RegistryActions.createRegistry, state => ({
        ...state,
        saving: onLoadableLoad(state.saving),
    })),
    on(RegistryActions.createRegistrySuccess, (state, { registry }) => ({
        ...state,
        registries: [...state.registries, registry],
        saving: onLoadableSuccess(state.saving),
    })),
    on(RegistryActions.createRegistryFailure, (state, { error }) => ({
        ...state,
        saving: onLoadableError(state.saving, error),
    })),

    on(RegistryActions.updateRegistry, state => ({
        ...state,
        saving: onLoadableLoad(state.saving),
    })),
    on(RegistryActions.updateRegistrySuccess, (state, { registry }) => ({
        ...state,
        registries: state.registries.map(r => {
            if (r.id !== registry.id) {
                return r;
            }
            return registry;
        }),
        saving: onLoadableSuccess(state.saving),
    })),
    on(RegistryActions.updateRegistryFailure, (state, { error }) => ({
        ...state,
        saving: onLoadableError(state.saving, error),
    })),

    on(RegistryActions.deleteRegistry, state => ({
        ...state,
        deleting: onLoadableLoad(state.deleting),
    })),
    on(RegistryActions.deleteRegistrySuccess, (state, { response }) => ({
        ...state,
        registries: state.registries.filter(r => r.id !== response.deleted_registry_id),
        deleting: onLoadableSuccess(state.deleting),
    })),
    on(RegistryActions.deleteRegistryFailure, (state, { error }) => ({
        ...state,
        deleting: onLoadableError(state.deleting, error),
    })),

    on(RegistryActions.loadSelectedRegistry, state => ({
        ...state,
        selectedRegistryLoading: onLoadableLoad(state.selectedRegistryLoading),
    })),
    on(RegistryActions.loadSelectedRegistrySuccess, (state, { registry }) => ({
        ...state,
        selectedRegistry: registry,
        selectedRegistryLoading: onLoadableSuccess(state.selectedRegistryLoading),
    })),
    on(RegistryActions.loadSelectedRegistryFailure, (state, { error }) => ({
        ...state,
        selectedRegistryLoading: onLoadableError(state.selectedRegistryLoading, error),
    })),
    on(RegistryActions.clearSelectedRegistry, state => ({
        ...state,
        selectedRegistry: null,
        selectedRegistryLoading: createDefaultLoadable(),
        entries: createDefaultILoadableArray(),
        entriesRegistryId: null,
        entryJobs: createDefaultILoadableArray(),
        entryJobsRegistryId: null,
        entryJobTotals: { pending: 0, failed: 0 },
        currentJobFilter: null,
    })),
    on(RegistryActions.setSelectedRegistry, (state, { registry }) => ({
        ...state,
        selectedRegistry: registry,
    })),

    on(RegistryActions.loadEntries, (state, { registryId }) => ({
        ...state,
        entries: onLoadableLoad(state.entries),
        entriesRegistryId: registryId,
    })),
    on(RegistryActions.loadEntriesSuccess, (state, { registryId, entries }) => ({
        ...state,
        entries: { ...onLoadableSuccess(state.entries), items: entries },
        entriesRegistryId: registryId,
    })),
    on(RegistryActions.loadEntriesFailure, (state, { registryId, error }) => ({
        ...state,
        entries: onLoadableError(state.entries, error),
        entriesRegistryId: registryId,
    })),

    on(RegistryActions.loadEntryJobs, (state, { registryId, status }) => ({
        ...state,
        entryJobs: onLoadableLoad(state.entryJobs),
        entryJobsRegistryId: registryId,
        currentJobFilter: status ?? null,
    })),
    on(RegistryActions.loadEntryJobsSuccess, (state, { registryId, status, entryJobs }) => {
        const isUnfiltered = status === undefined || status === null;
        return {
            ...state,
            entryJobs: { ...onLoadableSuccess(state.entryJobs), items: entryJobs },
            entryJobsRegistryId: registryId,
            currentJobFilter: status ?? null,
            entryJobTotals: isUnfiltered
                ? {
                    pending: entryJobs.filter(j => j.status === 'pending' || j.status === 'running').length,
                    failed: entryJobs.filter(j => j.status === 'failed').length,
                }
                : state.entryJobTotals,
        };
    }),
    on(RegistryActions.loadEntryJobsFailure, (state, { registryId, status, error }) => ({
        ...state,
        entryJobs: onLoadableError(state.entryJobs, error),
        entryJobsRegistryId: registryId,
        currentJobFilter: status ?? null,
    })),

    on(RegistryActions.createEntry, state => ({
        ...state,
        creatingEntry: onLoadableLoad(state.creatingEntry),
    })),
    on(RegistryActions.createEntrySuccess, (state, { entryJob }) => ({
        ...state,
        entryJobs: { ...onLoadableSuccess(state.entryJobs), items: [entryJob, ...state.entryJobs.items] },
        creatingEntry: onLoadableSuccess(state.creatingEntry),
    })),
    on(RegistryActions.createEntryFailure, (state, { error }) => ({
        ...state,
        creatingEntry: onLoadableError(state.creatingEntry, error),
    })),

    on(RegistryActions.bulkCreateEntries, state => ({
        ...state,
        bulkCreatingEntries: onLoadableLoad(state.bulkCreatingEntries),
    })),
    on(RegistryActions.bulkCreateEntriesSuccess, (state, { result }) => ({
        ...state,
        entryJobs: {
            ...onLoadableSuccess(state.entryJobs),
            items: [...createdEntryJobs(result), ...state.entryJobs.items],
        },
        bulkCreatingEntries: onLoadableSuccess(state.bulkCreatingEntries),
    })),
    on(RegistryActions.bulkCreateEntriesFailure, (state, { error }) => ({
        ...state,
        bulkCreatingEntries: onLoadableError(state.bulkCreatingEntries, error),
    })),
    on(RegistryActions.clearBulkCreatingEntries, state => ({
        ...state,
        bulkCreatingEntries: createDefaultLoadable(),
    })),

    on(RegistryActions.updateEntry, state => ({
        ...state,
        updatingEntry: onLoadableLoad(state.updatingEntry),
    })),
    on(RegistryActions.updateEntrySuccess, (state, { entryJob }) => ({
        ...state,
        entryJobs: { ...onLoadableSuccess(state.entryJobs), items: [entryJob, ...state.entryJobs.items] },
        updatingEntry: onLoadableSuccess(state.updatingEntry),
    })),
    on(RegistryActions.updateEntryFailure, (state, { error }) => ({
        ...state,
        updatingEntry: onLoadableError(state.updatingEntry, error),
    })),

    on(RegistryActions.deleteEntries, state => ({
        ...state,
        deletingEntries: onLoadableLoad(state.deletingEntries),
    })),
    on(RegistryActions.deleteEntriesSuccess, (state, { entryJobs }) => ({
        ...state,
        entryJobs: { ...onLoadableSuccess(state.entryJobs), items: [...entryJobs, ...state.entryJobs.items] },
        deletingEntries: onLoadableSuccess(state.deletingEntries),
    })),
    on(RegistryActions.deleteEntriesFailure, (state, { error }) => ({
        ...state,
        deletingEntries: onLoadableError(state.deletingEntries, error),
    })),

    on(RegistryActions.clearSaving, state => ({
        ...state,
        saving: createDefaultLoadable(),
    })),
    on(RegistryActions.clearDeleting, state => ({
        ...state,
        deleting: createDefaultLoadable(),
    })),
    on(RegistryActions.clearCreatingEntry, state => ({
        ...state,
        creatingEntry: createDefaultLoadable(),
    })),
    on(RegistryActions.clearUpdatingEntry, state => ({
        ...state,
        updatingEntry: createDefaultLoadable(),
    })),
);
